import { API_BASE } from '@/app.config'
import type {
  ApiErrorBody,
  ApiErrorCode,
  ChangePasswordRequest,
  CreateMemberRequest,
  LoginResponse,
  Member,
  PullResponse,
  PushResponse,
  SyncOp,
  UpdateMemberRequest,
  UserPublic,
} from '@cadoan/shared'

/**
 * The only module that talks to the network.
 *
 * Every failure becomes an `ApiFailure` with a `kind` the store can branch on:
 * `network` (no connection, timeout — try again later, data stays queued),
 * `api` (the Worker answered with an error code), `server` (5xx or garbage).
 */

const TIMEOUT_MS = 15_000

export type FailureKind = 'network' | 'api' | 'server'

export class ApiFailure extends Error {
  constructor(
    readonly kind: FailureKind,
    readonly status: number,
    readonly code: ApiErrorCode | null,
    readonly retryAfterSec?: number,
  ) {
    super(code ?? kind)
  }
}

async function request<T>(
  method: string,
  path: string,
  opts: { token?: string | null; body?: unknown } = {},
): Promise<T> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)
  let res: Response
  try {
    res = await fetch(`${API_BASE}/api${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(opts.token ? { Authorization: `Bearer ${opts.token}` } : {}),
      },
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: controller.signal,
    })
  } catch {
    throw new ApiFailure('network', 0, null)
  } finally {
    clearTimeout(timer)
  }

  if (res.status === 204) return undefined as T
  let data: unknown = null
  try {
    data = await res.json()
  } catch {
    throw new ApiFailure(res.status >= 500 ? 'server' : 'network', res.status, null)
  }
  if (!res.ok) {
    const err = (data as ApiErrorBody | null)?.error
    if (!err?.code || res.status >= 500)
      throw new ApiFailure('server', res.status, err?.code ?? null)
    throw new ApiFailure('api', res.status, err.code, err.retryAfterSec)
  }
  return data as T
}

export const api = {
  login: (username: string, password: string) =>
    request<LoginResponse>('POST', '/auth/login', { body: { username, password, app: 'mobile' } }),
  me: (token: string) => request<UserPublic>('GET', '/auth/me', { token }),
  logout: (token: string) => request<void>('POST', '/auth/logout', { token }),
  changePassword: (token: string, body: ChangePasswordRequest) =>
    request<void>('POST', '/auth/change-password', { token, body }),
  pull: (token: string, since: number) =>
    request<PullResponse>('GET', `/sync?since=${since}`, { token }),
  push: (token: string, ops: SyncOp[]) =>
    request<PushResponse>('POST', '/sync/push', { token, body: { ops } }),
  createMember: (token: string, body: CreateMemberRequest) =>
    request<Member>('POST', '/members', { token, body }),
  updateMember: (token: string, id: string, body: UpdateMemberRequest) =>
    request<Member>('PATCH', `/members/${id}`, { token, body }),
}

export type MemberPatch = UpdateMemberRequest

export type Api = typeof api
